import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parseAutomatedTestingSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage automated testing versions
 * Stores and tracks different versions of automated tests from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * - Local edit detection via isLocalUpdateRef
 * - Proper version tracking without creating duplicates on edit
 */
const useAutomatedTestingVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for automated testing version management
  const automatedTestingConfig = {
    // Determine if a message contains automated testing content
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      return content.includes('Automated Testing') ||
             content.includes('Test Automation') ||
             content.includes('Automation Framework') ||
             content.includes('Selenium') ||
             content.includes('Playwright') ||
             content.includes('Cypress') ||
             content.includes('CI/CD') ||
             content.includes('###');
    },

    // Parse automated testing sections from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseAutomatedTestingSections(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagementV1 based on messageIndex
    extractVersions: (msg, parsedTestCases, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        testCases: parsedTestCases,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the V1 version management hook that supports isLocalUpdateRef
  const versionData = useVersionManagementV1(automatedTestingConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      testCases: versionData.currentVersion.parsedContent || versionData.currentVersion.testCases
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to automated testing-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useAutomatedTestingVersions;
